import {createContext, Dispatch, SetStateAction, useEffect, useState} from 'react'
import qs from 'qs'
import {ID, QueryResponseContextProps, QueryState} from './models'
import {LS_KEYS, TABLE_LIST_FILTER_KEY} from './consts'

function createResponseContext<T>(initialState: QueryResponseContextProps<T>) {
  return createContext(initialState)
}

function isNotEmpty(obj: unknown) {
  return obj !== undefined && obj !== null && obj !== ''
}

// Example: page=1&itemsPerPage=10&order[id]=desc&search=a&name=a&online=false
function stringifyRequestQuery(state: QueryState): string {
  const pagination = qs.stringify(state, {filter: ['page', 'itemsPerPage'], skipNulls: true})
  const sort =
    isNotEmpty(state.order) && isNotEmpty(state.sort) ? `order[${state.sort}]=${state.order}` : ''
  const search = isNotEmpty(state.search)
    ? qs.stringify(state, {filter: ['search'], skipNulls: true})
    : ''

  const filter = state.filter
    ? Object.entries(state.filter as Object)
        .filter((obj) => isNotEmpty(obj[1]))
        .map((obj) => {
          const bracketMatch = obj[0].match(/\[(.*?)\]/)
          if (bracketMatch) {
            const keyWithoutBrackets = obj[0].replace(/\[(.*?)\]/, '')
            const bracketContent = bracketMatch[1]
            return `filter[${keyWithoutBrackets}][${bracketContent}]=${obj[1]}`
          } else {
            return `filter[${obj[0]}]=${obj[1]}`
          }
        })
        .join('&')
    : ''

  return [pagination, sort, search, filter].filter((f) => f).join('&')
}

function parseRequestQuery(query: string): QueryState {
  const cache: unknown = qs.parse(query)
  return cache as QueryState
}

function calculatedGroupingIsDisabled<T>(isLoading: boolean, data: Array<T> | undefined): boolean {
  if (isLoading) {
    return true
  }

  return !data || !data.length
}

function calculateIsAllDataSelected<T>(data: Array<T> | undefined, selected: Array<ID>): boolean {
  if (!data) {
    return false
  }

  return data.length > 0 && data.length === selected.length
}

function groupingOnSelect(
  id: ID,
  selected: Array<ID>,
  setSelected: Dispatch<SetStateAction<Array<ID>>>
) {
  if (!id) {
    return
  }

  if (selected.includes(id)) {
    setSelected(selected.filter((itemId) => itemId !== id))
  } else {
    const updatedSelected = [...selected]
    updatedSelected.push(id)
    setSelected(updatedSelected)
  }
}

function groupingOnSelectAll<T>(
  isAllSelected: boolean,
  setSelected: Dispatch<SetStateAction<Array<ID>>>,
  data?: Array<T & {id?: ID}>
) {
  if (isAllSelected) {
    setSelected([])
    return
  }

  if (!data || !data.length) {
    return
  }

  setSelected(data.filter((item) => item.id).map((item) => item.id))
}
// pagination from LS

function getSavedPagination() {
  const data = localStorage.getItem(LS_KEYS.PAGINATION_KEY)
  if (data && (typeof data === 'string' || typeof data === 'number')) {
    return +JSON.parse(data)
  } else {
    return 10
  }
}

// table filters

function getSavedFilter(listName: string) {
  const data = localStorage.getItem(TABLE_LIST_FILTER_KEY(listName))
  if (data && typeof data === 'string' && data !== 'undefined') {
    return JSON.parse(data)
  } else {
    return undefined
  }
}

// Hook
function useDebounce(value: string | undefined, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler)
      }
    },
    [value, delay] // Only re-call effect if value or delay changes
  )
  return debouncedValue
}

function selectDebounce<T extends (...args: any[]) => any>(func: T, delay: number) {
  let timeoutId: NodeJS.Timeout
  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(this, args), delay)
  }
}

const processApiErrors = (
  errors: Record<string, string|string[]> | undefined,
  intl: any
): Record<string, string> => {
  const formattedErrors: Record<string, string> = {};

  if (!errors) return formattedErrors;
  Object.entries(errors).forEach(([fieldName, errorMessages]) => {
    if (Array.isArray(errorMessages) && errorMessages.length > 0) {
      formattedErrors[fieldName] = errorMessages
        .map(errorId => intl.formatMessage({ id: errorId }, { defaultMessage: errorId }))
        .join('\n');
    }
    else if (typeof errorMessages === 'string') {
      formattedErrors[fieldName] = intl.formatMessage(
        { id: errorMessages },
        { defaultMessage: errorMessages }
      );
    }
  });

  return formattedErrors;
};

function isValidDate(d:any) {
  // @ts-ignore
  return d instanceof Date && !isNaN(d);
}
const getEmptyTranslations = (translations: any) => {
  if (!translations) return {}

  return Object.keys(translations).reduce((acc, lang) => {
    acc[lang] = {}
    return acc
  }, {} as Record<string, {}>)
}

export {
  createResponseContext,
  stringifyRequestQuery,
  parseRequestQuery,
  calculatedGroupingIsDisabled,
  calculateIsAllDataSelected,
  groupingOnSelect,
  groupingOnSelectAll,
  useDebounce,
  selectDebounce,
  isNotEmpty,
  getSavedPagination,
  getSavedFilter,
  processApiErrors,
  isValidDate,
  getEmptyTranslations,
}
